// What the login form shows when POST /auth/login/ fails.
// A 401 carries `detail`. A 400 names the field instead: {"password":["This field may not be blank."]}.
// A Map, not a plain object: a body key such as "constructor" must not find Object.prototype (#117).
const FIELD_LABELS = new Map([
  ['email', 'Email'],
  ['password', 'Password'],
]);

export const NO_RESPONSE_MESSAGE = "Can't reach the server. Check your connection and try again.";
export const SERVER_ERROR_MESSAGE = 'The server had a problem. Try again in a few minutes.';
export const FALLBACK_MESSAGE = 'Sign-in failed. Try again.';

// The parts of an axios error this reads.
// Axios sets `isAxiosError`, `request` and `code` on a request that got no response.
// A plain Error thrown by our own code has none of them.
// An axios cancellation (`ERR_CANCELED`) has them too, so it reads as unreachable. The login page never cancels.
// A caller that does should check `axios.isCancel` first.
type FailedRequest = {
  isAxiosError?: boolean;
  request?: unknown;
  code?: unknown;
  response?: { status?: number; data?: unknown };
};

const isRequestWithoutResponse = (err: FailedRequest): boolean =>
  err.isAxiosError === true || (err.request !== undefined && err.request !== null) || typeof err.code === 'string';

export type LoginFieldFaults = { email: boolean; password: boolean };

const NO_FIELDS: LoginFieldFaults = { email: false, password: false };
const BOTH_FIELDS: LoginFieldFaults = { email: true, password: true };

export type LoginFailure = { message: string; fields: LoginFieldFaults };

const faultsForField = (field: string): LoginFieldFaults => {
  if (field === 'email') return { email: true, password: false };
  if (field === 'password') return { email: false, password: true };
  return NO_FIELDS;
};

// One walk of the body, so the shown message and the marked field stay the same parse.
export const loginFailure = (err: unknown): LoginFailure => {
  const failed = err && typeof err === 'object' ? (err as FailedRequest) : undefined;
  const response = failed?.response;
  if (!response) {
    // A request that got no response: offline, CORS, timeout. The credentials were never checked.
    if (failed && isRequestWithoutResponse(failed)) return { message: NO_RESPONSE_MESSAGE, fields: NO_FIELDS };
    // Anything else is a throw in our own code, before the request or after a 200 (#117).
    // The server may well have answered, so don't claim it is unreachable.
    return { message: FALLBACK_MESSAGE, fields: NO_FIELDS };
  }

  const { status = 0, data } = response;
  // A 5xx is the server's problem whatever its body says (#117).
  // An error page, or a JSON body that happens to carry a list of strings, is not a field error.
  if (status >= 500) return { message: SERVER_ERROR_MESSAGE, fields: NO_FIELDS };
  // A 401 is the credential pair, including when the body names one field or names nothing.
  const pair: LoginFieldFaults = status === 401 ? BOTH_FIELDS : NO_FIELDS;

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const { detail } = record;
    if (typeof detail === 'string' && detail !== '') return { message: detail, fields: pair };

    for (const [field, value] of Object.entries(record)) {
      const text = Array.isArray(value) ? value.find(item => typeof item === 'string' && item !== '') : undefined;
      if (typeof text !== 'string') continue;
      const label = FIELD_LABELS.get(field);
      const message = label ? `${label}: ${text}` : text;
      return { message, fields: status === 401 ? BOTH_FIELDS : faultsForField(field) };
    }
  }
  return { message: FALLBACK_MESSAGE, fields: pair };
};

export const loginErrorMessage = (err: unknown): string => loginFailure(err).message;
